/**
 * When the person last did anything in the app: any input or navigation, in any
 * of their tabs of it.
 *
 * Inputs are the deliberate ones (a press, a key, a scroll, a touch); a pointer
 * merely resting over the page is not someone there. A navigation counts when
 * the person made it: the click, key or tap that followed a link is already an
 * input, and back/forward arrive as popstate. The app's own router.push (the
 * agent opening a task, a spoken Line command) is not counted, which is why
 * nothing here watches the pathname.
 *
 * The computer's sound keeps a paid box awake, and someone who walked away an
 * hour ago is not listening, so the sound stops an hour after the last thing
 * they did (ComputerSoundPlayer). "Anywhere in the app" includes another tab of
 * it, so each tab shares its stamp through localStorage (at most every few
 * seconds) and the reader takes the newest. Browser storage can be missing or
 * throw; this tab's own stamp still stands.
 */

const STORAGE_KEY = "vestra.person-activity-at";
/** How often a tab shares its stamp with the others. */
const SHARE_EVERY_MS = 5_000;
const INPUTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

let lastAt = Date.now();
let sharedAt = 0;

/** The person did something in this tab just now. */
export function notePersonActivity(now: number = Date.now()): void {
  lastAt = now;
  if (now - sharedAt < SHARE_EVERY_MS) return;
  sharedAt = now;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    // This tab's stamp stands without the shared one.
  }
}

/** The newest activity this tab or any other tab of the app has seen. */
export function lastPersonActivityAt(): number {
  let shared = 0;
  try {
    shared = Number(window.localStorage.getItem(STORAGE_KEY)) || 0;
  } catch {
    // Storage unreadable: this tab's own stamp is the answer.
  }
  return Math.max(lastAt, shared);
}

/** Count every input in this tab, and history navigation. Returns the uninstall. */
export function watchPersonActivity(): () => void {
  const onActivity = () => notePersonActivity();
  const opts = { capture: true, passive: true } as const;
  for (const type of INPUTS) window.addEventListener(type, onActivity, opts);
  window.addEventListener("popstate", onActivity);
  return () => {
    for (const type of INPUTS) {
      window.removeEventListener(type, onActivity, opts);
    }
    window.removeEventListener("popstate", onActivity);
  };
}
